import { Injectable } from '@angular/core';
import { NgRedux } from '@angular-redux/store';
import { filter } from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { IAppState, User } from '@core/redux/stores';
import { AuthTrackingService, ModalService, AuthenticationService, CiamFlagsService } from '@core/services';
import { ProxyService } from '@core/services/proxy.service';
import { FeatureFlagService } from '@core/services/statsig';
import { isAuth0Token } from '@core/services/jit-migration.service';
import { NUDGE_PLATFORM } from '@core/services/migration-nudge-state.service';
import { ApiRoutes } from '@shared/models/configs/api.endpoints';

/** Name and metadata shape are shared with MAUI HomeViewModel.TryRunSessionMigrationAsync. */
const SOCIAL_SESSION_MIGRATION_EVENT = 'social_session_migration';

const STORAGE_KEY_LAST_FAILED_AT = 'socialMigration.lastFailedAt';
const STORAGE_KEY_VERIFICATION_LAST_SHOWN = 'socialVerificationModalLastShown';
const THINKTECTURE_SOCIAL_TYPES = ['google', 'apple', 'facebook'];
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

@Injectable()
export class SocialMigrationService {
  private subscription: Subscription | null = null;
  private isInFlight = false;

  constructor(
    private ngRedux: NgRedux<IAppState>,
    private authTrackingService: AuthTrackingService,
    private modalService: ModalService,
    private authenticationService: AuthenticationService,
    private proxy: ProxyService,
    private router: Router,
    private ciamFlagsService: CiamFlagsService,
    private featureFlags: FeatureFlagService
  ) {}

  /**
   * Background silent swap: if user is logged in with Thinktecture social token,
   * call migrate_session to swap for Auth0 tokens.
   * Skip when token is a JWT (Thinktecture uses opaque reference tokens).
   */
  checkAndMigrateSilently(): void {
    if (!this.ciamFlagsService.isSocialMigration()) return;
    if (!this.authTrackingService.isLoggedIn()) return;

    const state = this.ngRedux.getState();
    const user = state?.user as User | undefined;
    const token = this.authTrackingService.getAuthToken();

    if (!user || !token) return;
    if (!THINKTECTURE_SOCIAL_TYPES.includes(user.mainAccountType?.toLowerCase())) return;
    if (isAuth0Token(token)) return;
    if (token.indexOf('.') >= 0) return;
    if (this.isInFlight) return;

    const lastFailedAt = this.getLastFailedAt();
    if (lastFailedAt != null && Date.now() - lastFailedAt < this.getRetryIntervalMs()) return;

    const provider = user.mainAccountType.toLowerCase();

    this.isInFlight = true;
    this.proxy.httpPost(ApiRoutes.AUTH0_MIGRATE_SESSION, {}).subscribe(
      (response: any) => {
        this.isInFlight = false;
        const body = response?.body ?? response;
        if (!body?.success || !(body?.accessToken ?? body?.AccessToken)) {
          this.recordFailedAttempt();
          this.logMigrationAttempt(false, provider);
          return;
        }
        this.clearLastFailedAt();
        this.authenticationService.setAuth0TokensFromOtpVerify(
          {
            accessToken: body.accessToken ?? body.AccessToken,
            refreshToken: body.refreshToken ?? body.RefreshToken,
            expiresIn: body.expiresIn ?? body.ExpiresIn
          }
        );
        this.logMigrationAttempt(true, provider);
      },
      () => {
        this.isInFlight = false;
        this.recordFailedAttempt();
        this.logMigrationAttempt(false, provider);
      }
    );
  }

  /**
   * The swap is invisible by design, so this event is the only evidence it ran. Reported once per
   * attempt to match MAUI, which logs the same name whether the call succeeded or failed.
   */
  private logMigrationAttempt(success: boolean, provider: string): void {
    try {
      this.featureFlags.logEvent(SOCIAL_SESSION_MIGRATION_EVENT, success ? 1 : 0, {
        success: String(success),
        provider,
        platform: NUDGE_PLATFORM
      });
    } catch {
      //telemetry must never break the silent swap
    }
  }

  private getRetryIntervalMs(): number {
    const hours = this.ciamFlagsService.getSocialMigrationRetryHours();
    return hours * 60 * 60 * 1000;
  }

  private getLastFailedAt(): number | null {
    const val = localStorage.getItem(STORAGE_KEY_LAST_FAILED_AT);
    if (val == null) return null;
    const n = parseInt(val, 10);
    return isNaN(n) ? null : n;
  }

  private recordFailedAttempt(): void {
    localStorage.setItem(STORAGE_KEY_LAST_FAILED_AT, String(Date.now()));
  }

  private clearLastFailedAt(): void {
    localStorage.removeItem(STORAGE_KEY_LAST_FAILED_AT);
  }

  recordVerificationModalDismissed(): void {
    localStorage.setItem(STORAGE_KEY_VERIFICATION_LAST_SHOWN, String(Date.now()));
  }

  clearVerificationLastShownTimestamp(): void {
    localStorage.removeItem(STORAGE_KEY_VERIFICATION_LAST_SHOWN);
  }

  private getVerificationLastShownTimestamp(): number | null {
    const val = localStorage.getItem(STORAGE_KEY_VERIFICATION_LAST_SHOWN);
    if (val == null) return null;
    const n = parseInt(val, 10);
    return isNaN(n) ? null : n;
  }

  /**
   * On each NavigationEnd: silent Thinktecture social -> Auth0 swap only (no verification nudge).
   */
  private onNavigationEnd(): void {
    this.checkAndMigrateSilently();
  }

  /**
   * Run the same checks as on NavigationEnd. Call on app init (with delay).
   */
  checkOnInit(): void {
    this.onNavigationEnd();
  }

  startRouterWatch(): void {
    if (this.subscription) return;
    this.subscription = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.onNavigationEnd());
  }

  stopRouterWatch(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }
}
